import React, { useRef, useState } from 'react';
import { X, RotateCcw, RotateCw, Eye } from 'lucide-react';
import resumeAPI from '../services/api';

/**
 * Image Editor Modal with Crop, Filter, and Adjust tabs
 * Features: Rotate left/right, Zoom, Straighten, Visibility settings
 */
const ImageEditorModal = ({ isOpen, onClose, imageUrl, onSave, title = "Edit photo", showVisibility = true, imageType = 'avatar' }) => {
  const [activeTab, setActiveTab] = useState('crop');
  const [zoom, setZoom] = useState(1);
  const [straighten, setStraighten] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibility, setVisibility] = useState('anyone');
  const [selectedFilter, setSelectedFilter] = useState('original');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [vignette, setVignette] = useState(0);
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const previewSize = 300;

  if (!isOpen) return null;

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSave = async () => {
    // Compress and apply transformations to reduce file size
    // Output as BLOB (binary) instead of base64 for direct S3 upload
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      const maxSize = 800;
      const outputSize = maxSize;
      const baseScale = Math.min(previewSize / img.width, previewSize / img.height);
      const scaleToOutput = outputSize / previewSize;
      const zoomScale = baseScale * zoom * scaleToOutput;

      canvas.width = outputSize;
      canvas.height = outputSize;

      ctx.save();
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outputSize, outputSize);
      ctx.translate(outputSize / 2 + pan.x * scaleToOutput, outputSize / 2 + pan.y * scaleToOutput);
      ctx.rotate((rotation + straighten) * Math.PI / 180);
      ctx.scale(zoomScale, zoomScale);

      // Apply filter and adjustments
      const filterStyle = filters.find(f => f.id === selectedFilter)?.filter || 'none';
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) ${filterStyle}`;

      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();

      // Convert to Blob and upload to S3
      canvas.toBlob(async (blob) => {
        if (!blob) {
          console.error('Failed to create image blob');
          // Fallback to original
          onSave({
            imageUrl,
            zoom,
            straighten,
            rotation,
            visibility,
            filter: selectedFilter,
            brightness,
            contrast,
            saturation,
            vignette
          });
          onClose();
          return;
        }

        try {
          // Convert blob to File for S3 upload
          const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
          
          // Upload to S3 and get CloudFront URL
          const cloudFrontUrl = await resumeAPI.uploadImageToS3(file, imageType);
          
          // Pass CloudFront URL instead of blob
          onSave({
            imageUrl: cloudFrontUrl,
            zoom,
            straighten,
            rotation,
            visibility,
            filter: selectedFilter,
            brightness,
            contrast,
            saturation,
            vignette
          });
          onClose();
        } catch (error) {
          console.error('Failed to upload edited image to S3:', error);
          alert('Failed to save edited image. Please try again.');
          // Don't close modal on error so user can retry
        }
      }, 'image/jpeg', 0.85);

    } catch (error) {
      console.error('Error processing image:', error);
      // Fallback to original if processing fails
      onSave({
        imageUrl,
        zoom,
        straighten,
        rotation,
        visibility,
        filter: selectedFilter,
        brightness,
        contrast,
        saturation,
        vignette
      });
      onClose();
    }
  };

  const filters = [
    { id: 'original', name: 'Original', filter: 'none' },
    { id: 'studio', name: 'Studio', filter: 'contrast(1.1) saturate(1.2)' },
    { id: 'spotlight', name: 'Spotlight', filter: 'brightness(1.1) contrast(1.15)' },
    { id: 'prime', name: 'Prime', filter: 'saturate(1.3) contrast(1.1)' },
    { id: 'classic', name: 'Classic', filter: 'sepia(0.3) contrast(1.1)' },
    { id: 'edge', name: 'Edge', filter: 'contrast(1.3) saturate(0.8)' },
    { id: 'luminate', name: 'Luminate', filter: 'brightness(1.2) saturate(1.1)' }
  ];

  const getImageStyle = () => {
    const filterStyle = filters.find(f => f.id === selectedFilter)?.filter || 'none';
    const adjustStyle = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    const vignetteStyle = vignette > 0 ? `radial-gradient(circle, transparent ${100 - vignette}%, rgba(0,0,0,${vignette / 100}) 100%)` : 'none';
    
    return {
      transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation + straighten}deg) scale(${zoom})`,
      transformOrigin: 'center',
      filter: activeTab === 'filter' ? filterStyle : adjustStyle,
      transition: 'transform 0.3s ease, filter 0.3s ease'
    };
  };

  const handlePointerDown = (e) => {
    if (!imageUrl) return;
    e.preventDefault();
    dragState.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y
    };
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (!dragState.current.isDragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPan({
      x: dragState.current.originX + dx,
      y: dragState.current.originY + dy
    });
  };

  const handlePointerUp = (e) => {
    if (!dragState.current.isDragging) return;
    dragState.current.isDragging = false;
    if (e.currentTarget.releasePointerCapture) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const visibilityOptions = [
    {
      id: '1st-degree',
      label: '1st-degree connections only',
      description: 'LinkedIn members connected directly to you.'
    },
    {
      id: 'network',
      label: 'Your network',
      description: 'Only people connected up to three degrees away from you will be able to see this.'
    },
    {
      id: 'all-members',
      label: 'All LinkedIn members',
      description: 'Members signed into LinkedIn, including everyone in your network.'
    },
    {
      id: 'anyone',
      label: 'Anyone',
      description: 'Anyone on or off LinkedIn.'
    }
  ];

  return (
    <>
      {/* Main Editor Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col relative z-[201]" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            {['Crop', 'Filter', 'Adjust'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.toLowerCase()
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px]">
              {/* Image Preview */}
              <div className="flex items-center justify-center bg-gray-100 p-8 min-h-[400px]">
                <div
                  className="relative rounded-full overflow-hidden shadow-lg cursor-grab active:cursor-grabbing"
                  style={{
                    width: `${previewSize}px`,
                    height: `${previewSize}px`,
                    touchAction: 'none'
                  }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="Preview"
                      className="w-full h-full object-contain"
                      style={getImageStyle()}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-500">No image</span>
                    </div>
                  )}
                  {vignette > 0 && (
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `radial-gradient(circle, transparent ${100 - vignette}%, rgba(0,0,0,${vignette / 100}) 100%)`
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Controls Panel */}
              <div className="p-6 bg-white border-l border-gray-200">
                {activeTab === 'crop' && (
                  <div className="space-y-6">
                    {/* Rotation Buttons */}
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={handleRotateLeft}
                        className="p-3 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Rotate left"
                      >
                        <RotateCcw className="w-6 h-6 text-gray-700" />
                      </button>
                      <button
                        onClick={handleRotateRight}
                        className="p-3 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Rotate right"
                      >
                        <RotateCw className="w-6 h-6 text-gray-700" />
                      </button>
                    </div>

                    {/* Zoom Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zoom
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0.5x</span>
                        <span>{zoom.toFixed(1)}x</span>
                        <span>2x</span>
                      </div>
                    </div>

                    {/* Straighten Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Straighten
                      </label>
                      <input
                        type="range"
                        min="-45"
                        max="45"
                        step="1"
                        value={straighten}
                        onChange={(e) => setStraighten(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>-45°</span>
                        <span>{straighten}°</span>
                        <span>45°</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'filter' && (
                  <div className="grid grid-cols-2 gap-4">
                    {filters.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => setSelectedFilter(filter.id)}
                        className={`relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          selectedFilter === filter.id
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt={filter.name}
                              className="w-full h-full object-cover"
                              style={{ filter: filter.filter }}
                            />
                          )}
                        </div>
                        <span className={`text-sm font-medium ${
                          selectedFilter === filter.id ? 'text-primary-600' : 'text-gray-700'
                        }`}>
                          {filter.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'adjust' && (
                  <div className="space-y-6">
                    {/* Brightness Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Brightness
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="1"
                        value={brightness}
                        onChange={(e) => setBrightness(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>{brightness}%</span>
                        <span>200%</span>
                      </div>
                    </div>

                    {/* Contrast Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contrast
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="1"
                        value={contrast}
                        onChange={(e) => setContrast(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>{contrast}%</span>
                        <span>200%</span>
                      </div>
                    </div>

                    {/* Saturation Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Saturation
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="1"
                        value={saturation}
                        onChange={(e) => setSaturation(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>{saturation}%</span>
                        <span>200%</span>
                      </div>
                    </div>

                    {/* Vignette Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vignette
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={vignette}
                        onChange={(e) => setVignette(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>{vignette}%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            {showVisibility && (
              <button
                onClick={() => setShowVisibilityModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {visibilityOptions.find(o => o.id === visibility)?.label || 'Anyone'}
                </span>
              </button>
            )}
            {!showVisibility && <div />}
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors font-medium"
            >
              Save photo
            </button>
          </div>
        </div>
      </div>

      {/* Visibility Settings Modal */}
      {showVisibilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]" onClick={() => setShowVisibilityModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative z-[301]" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Visibility</h3>
              <button
                onClick={() => setShowVisibilityModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Choose who can see your profile photo</p>
              <div className="space-y-3">
                {visibilityOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={option.id}
                      checked={visibility === option.id}
                      onChange={(e) => setVisibility(e.target.value)}
                      className="mt-1 w-5 h-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-600">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowVisibilityModal(false)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageEditorModal;
